import { useCallback, useEffect, useRef, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $findMatchingParent, mergeRegister } from '@lexical/utils'
import { useLingui } from '@lingui/react/macro'

import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListType,
  REMOVE_LIST_COMMAND
} from '@lexical/list'
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link'

import {
  ArrowMoveUpLeftIcon,
  ArrowMoveUpRightIcon,
  Heading01Icon,
  Heading02Icon,
  Heading03Icon,
  LeftToRightListBulletIcon,
  LeftToRightListNumberIcon,
  TextBoldIcon,
  TextIndentLessIcon,
  TextIndentMoreIcon,
  TextItalicIcon,
  TextStrikethroughIcon,
  TextUnderlineIcon
} from '@hugeicons/core-solid-rounded'

import {
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  LexicalNode,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND
} from 'lexical'

import {
  hasHeadingInSelection,
  INSERT_HEADING_COMMAND,
  REMOVE_HEADING_COMMAND
} from './HeadingPlugin'
import ToolbarButton from './ToolbarButton'
import LinkToolbarButton from './LinkToolbarButton'

const LowPriority = 1

function linkInSelection(selection: BaseSelection | null): string {
  if (!selection) return ''

  const nodes = Array.from(selection.getNodes())

  return (
    nodes.reduce<string | null>(function (link, node) {
      if ($isLinkNode(node)) return node.getURL()

      const parent = $findMatchingParent(node, node => $isLinkNode(node))

      if (parent) return parent.getURL()
      return link
    }, null) ?? ''
  )
}

function hasListInSelection(
  selection: BaseSelection | null,
  type: ListType
): boolean {
  if (!selection) return false

  const nodes = Array.from(selection.getNodes())

  const isListNode = (node: LexicalNode) =>
    $isListNode(node) && node.getListType() === type

  return nodes.some((node: LexicalNode) => {
    if (isListNode(node)) return true
    return !!$findMatchingParent(node, node => isListNode(node))
  })
}

export default function ToolbarPlugin() {
  const { t } = useLingui()

  const [editor] = useLexicalComposerContext()
  const toolbarRef = useRef(null)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isUnorderedList, setIsUnorderedList] = useState(false)
  const [isOrderedList, setIsOrderedList] = useState(false)
  const [isHeading1, setIsHeading1] = useState(false)
  const [isHeading2, setIsHeading2] = useState(false)
  const [isHeading3, setIsHeading3] = useState(false)
  const [link, setLink] = useState<string>('')

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()

    setLink(linkInSelection(selection))
    setIsUnorderedList(hasListInSelection(selection, 'bullet'))
    setIsOrderedList(hasListInSelection(selection, 'number'))
    setIsHeading1(hasHeadingInSelection(selection, 1))
    setIsHeading2(hasHeadingInSelection(selection, 2))
    setIsHeading3(hasHeadingInSelection(selection, 3))

    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
    }
  }, [])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateToolbar()
          return false
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        payload => {
          setCanUndo(payload)
          return false
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        payload => {
          setCanRedo(payload)
          return false
        },
        LowPriority
      )
    )
  }, [editor, $updateToolbar])

  if (!editor.isEditable()) return null

  return (
    <div
      className="flex max-w-full flex-row flex-wrap items-center justify-start gap-2 p-2 pb-4"
      ref={toolbarRef}
    >
      <div className="flex flex-row gap-0.5">
        <ToolbarButton
          disabled={!canUndo}
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          label={t`Undo`}
          Icon={ArrowMoveUpLeftIcon}
        />
        <ToolbarButton
          disabled={!canRedo}
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          label={t`Redo`}
          Icon={ArrowMoveUpRightIcon}
        />
      </div>
      <div className="flex flex-row gap-0.5">
        <ToolbarButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          label={t`Format Bold`}
          Icon={TextBoldIcon}
          active={isBold}
        />
        <ToolbarButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          label={t`Format Italic`}
          Icon={TextItalicIcon}
          active={isItalic}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
          }
          label={t`Format Underline`}
          Icon={TextUnderlineIcon}
          active={isUnderline}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
          }
          label={t`Format Strikethrough`}
          Icon={TextStrikethroughIcon}
          active={isStrikethrough}
        />
      </div>
      <div className="flex flex-row gap-0.5">
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(
              isHeading1 ? REMOVE_HEADING_COMMAND : INSERT_HEADING_COMMAND,
              1
            )
          }
          label={t`Format Heading 1`}
          Icon={Heading01Icon}
          active={isHeading1}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(
              isHeading2 ? REMOVE_HEADING_COMMAND : INSERT_HEADING_COMMAND,
              2
            )
          }
          label={t`Format Heading 2`}
          Icon={Heading02Icon}
          active={isHeading2}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(
              isHeading3 ? REMOVE_HEADING_COMMAND : INSERT_HEADING_COMMAND,
              3
            )
          }
          label={t`Format Heading 3`}
          Icon={Heading03Icon}
          active={isHeading3}
        />
      </div>
      <div className="flex flex-row gap-0.5">
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(
              isUnorderedList
                ? REMOVE_LIST_COMMAND
                : INSERT_UNORDERED_LIST_COMMAND,
              undefined
            )
          }
          label={t`Format Bullet List`}
          Icon={LeftToRightListBulletIcon}
          active={isUnorderedList}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(
              isOrderedList ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
              undefined
            )
          }
          label={t`Format Number List`}
          Icon={LeftToRightListNumberIcon}
          active={isOrderedList}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
          }
          label={t`Indent Line`}
          Icon={TextIndentMoreIcon}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
          }
          label={t`Un-Indent Line`}
          Icon={TextIndentLessIcon}
        />
      </div>
      <div className="flex flex-row gap-0.5">
        <LinkToolbarButton
          onAddLink={link =>
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, link ? link : null)
          }
          link={link}
        />
      </div>
    </div>
  )
}
